import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Mitarbeiter, ArbeitszeitAuswertung, ZeiterfassungEintrag, StempelTyp, Korrekturantrag } from '../types';
import zeiterfassungService from '../services/zeiterfassungService';
import { Icon, PageHeader, LoadingScreen, EmptyState, FilterZuruecksetzen, filterAktiv, fehlerText, th, td, tableScroll } from '../components/ui';
import { ankunft, markiereNeu } from '../utils/ankunft';
import { DatumFeld, UhrzeitFeld } from '../components/DatumZeit';

const monatsStart = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toLocaleDateString('sv-SE'); };
const heute = () => new Date().toLocaleDateString('sv-SE');
const fmt = (min: number) => `${Math.floor(Math.abs(min) / 60)}h ${(Math.abs(min) % 60).toString().padStart(2, '0')}m`;
const fmtSaldo = (min: number) => (min > 0 ? `+${fmt(min)}` : min < 0 ? `−${fmt(min)}` : '0h 00m');
const saldoColor = (min: number) => (min > 0 ? 'text-green-600' : min < 0 ? 'text-red-600' : 'text-gray-500');

/** Tag, auf den das Korrektur-Fenster eingegrenzt ist (aus der Tageszeile eines Mitarbeiters geöffnet). */
interface KorrekturTag {
  userId: number;
  mitarbeiterName: string;
  datum: string; // yyyy-MM-dd
  wochentag: string;
  sollBeginn?: string | null;
  sollEnde?: string | null;
}

interface KorrekturForm {
  id?: number;
  userId: number;
  typ: StempelTyp;
  zeitpunkt: string; // yyyy-MM-ddTHH:mm
  notiz: string;
}

const Zeiterfassung: React.FC = () => {
  const [von, setVon] = useState(monatsStart());
  const [bis, setBis] = useState(heute());
  const [userId, setUserId] = useState<number | ''>('');
  const [users, setUsers] = useState<Mitarbeiter[]>([]);
  const [auswertung, setAuswertung] = useState<ArbeitszeitAuswertung[]>([]);
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [expanded, setExpanded] = useState<number | null>(null);

  // Offene Korrekturanträge der Mitarbeiter
  const [antraege, setAntraege] = useState<Korrekturantrag[]>([]);

  // Korrektur-Modal
  const [korrekturOffen, setKorrekturOffen] = useState(false);
  const [korrekturTag, setKorrekturTag] = useState<KorrekturTag | null>(null);
  const [eintraege, setEintraege] = useState<ZeiterfassungEintrag[]>([]);
  const [form, setForm] = useState<KorrekturForm | null>(null);

  useEffect(() => { zeiterfassungService.getAktiveMitarbeiter().then(setUsers).catch(() => {}); }, []);

  const antraegeLaden = async () => {
    try { setAntraege(await zeiterfassungService.offeneAntraege()); } catch { /* ignore */ }
  };
  useEffect(() => { antraegeLaden(); }, []);

  const antragGenehmigen = async (a: Korrekturantrag) => {
    try {
      await zeiterfassungService.antragGenehmigen(a.id);
      toast.success(`Nachtrag für ${a.mitarbeiterName} gebucht`);
      await antraegeLaden();
      await laden();
    } catch (e: any) {
      toast.error(fehlerText(e, 'Nachtrag konnte nicht genehmigt werden'));
    }
  };

  const antragAblehnen = async (a: Korrekturantrag) => {
    const grund = window.prompt(`Nachtrag von ${a.mitarbeiterName} ablehnen – Grund (optional):`, '');
    if (grund === null) return;
    try {
      await zeiterfassungService.antragAblehnen(a.id, grund.trim());
      toast.success('Nachtrag abgelehnt');
      await antraegeLaden();
    } catch (e: any) {
      toast.error(fehlerText(e, 'Nachtrag konnte nicht abgelehnt werden'));
    }
  };

  const laden = async () => {
    setLoading(true);
    try {
      setAuswertung(await zeiterfassungService.auswertung(von, bis, userId === '' ? undefined : userId));
    } catch {
      toast.error('Auswertung konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { laden(); }, []);

  const pdfExportieren = async () => {
    setPdfLoading(true);
    try {
      await zeiterfassungService.auswertungPdf(von, bis, userId === '' ? undefined : userId);
    } catch {
      toast.error('PDF konnte nicht erzeugt werden');
    } finally {
      setPdfLoading(false);
    }
  };

  const eintraegeLaden = async (tag: KorrekturTag | null = korrekturTag): Promise<ZeiterfassungEintrag[]> => {
    try {
      // Für einen Tag chronologisch (Kommen vor Gehen), sonst neueste zuerst wie vom Backend geliefert
      const geladen = tag
        ? (await zeiterfassungService.eintraege(tag.datum, tag.datum, tag.userId)).sort((x, y) => x.zeitpunkt.localeCompare(y.zeitpunkt) || x.id - y.id)
        : await zeiterfassungService.eintraege(von, bis, userId === '' ? undefined : userId);
      setEintraege(geladen);
      return geladen;
    } catch {
      toast.error('Stempel konnten nicht geladen werden');
      return [];
    }
  };

  const oeffneKorrektur = async () => {
    setKorrekturTag(null);
    setForm(null);
    await eintraegeLaden(null);
    setKorrekturOffen(true);
  };

  /**
   * Öffnet die Korrektur für einen Tag eines Mitarbeiters. Fehlt dort offensichtlich ein Stempel (gar keiner,
   * oder vor heute ein Kommen ohne Gehen), steht gleich ein passender neuer bereit.
   */
  const oeffneTag = async (tag: KorrekturTag) => {
    setKorrekturTag(tag);
    setForm(null);
    const geladen = await eintraegeLaden(tag);
    setKorrekturOffen(true);
    const gehenFehlt = geladen[geladen.length - 1]?.typ === 'KOMMEN' && tag.datum < heute();
    if (geladen.length === 0 || gehenFehlt) setForm(vorschlag(tag, geladen));
  };

  const schliesseKorrektur = () => {
    setKorrekturOffen(false);
    setKorrekturTag(null);
    setForm(null);
  };

  /** Vorschlag für den nächsten Stempel: nach einem Kommen fehlt meist das Gehen. */
  const vorschlag = (tag: KorrekturTag, liste: ZeiterfassungEintrag[]): KorrekturForm => {
    const letzter = liste[liste.length - 1];
    const typ: StempelTyp = letzter?.typ === 'KOMMEN' ? 'GEHEN' : 'KOMMEN';
    const uhrzeit = typ === 'KOMMEN' ? (tag.sollBeginn || '08:00') : (tag.sollEnde || '16:30');
    return { userId: tag.userId, typ, zeitpunkt: `${tag.datum}T${uhrzeit.slice(0, 5)}`, notiz: '' };
  };

  const neuerEintrag = () => setForm(korrekturTag ? vorschlag(korrekturTag, eintraege) : {
    userId: userId === '' ? (users[0]?.id ?? 0) : userId,
    typ: 'KOMMEN',
    zeitpunkt: `${heute()}T08:00`,
    notiz: '',
  });

  const bearbeite = (e: ZeiterfassungEintrag) => setForm({
    id: e.id,
    userId: e.userId,
    typ: e.typ,
    zeitpunkt: e.zeitpunkt.replace(' ', 'T'),
    notiz: e.notiz ?? '',
  });

  const speichern = async () => {
    if (!form) return;
    const zeitpunkt = form.zeitpunkt.replace('T', ' ');
    try {
      if (form.id) {
        await zeiterfassungService.updateEintrag(form.id, { typ: form.typ, zeitpunkt, notiz: form.notiz || undefined });
        markiereNeu(`stempel-${form.id}`);
        toast.success('Stempel gespeichert');
      } else {
        const neu = await zeiterfassungService.addEintrag({ userId: form.userId, typ: form.typ, zeitpunkt, notiz: form.notiz || undefined });
        if (neu?.id) markiereNeu(`stempel-${neu.id}`);
        toast.success('Stempel angelegt');
      }
      setForm(null);
      await eintraegeLaden();
      await laden();
    } catch (e: any) {
      toast.error(fehlerText(e, 'Stempel konnte nicht gespeichert werden'));
    }
  };

  const loeschen = async (e: ZeiterfassungEintrag) => {
    if (!window.confirm(`Stempel „${e.mitarbeiterName}, ${e.zeitpunkt}“ löschen?`)) return;
    try {
      await zeiterfassungService.deleteEintrag(e.id);
      toast.success('Stempel gelöscht');
      await eintraegeLaden();
      await laden();
    } catch (err) {
      toast.error(fehlerText(err, 'Stempel konnte nicht gelöscht werden'));
    }
  };

  return (
    <div className="p-4 md:p-6">
      <PageHeader
        title="Zeiterfassung"
        actions={
          <button onClick={oeffneKorrektur} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-brand-600 text-brand-700 hover:bg-brand-50 text-sm font-medium">
            <Icon.Edit className="h-4 w-4" />Stempel korrigieren
          </button>
        }
      />

      {/* Offene Korrekturanträge */}
      {antraege.length > 0 && (
        <div className="bg-white rounded-lg shadow mb-6 border-l-4 border-yellow-400">
          <div className="p-4 border-b border-gray-100">
            <h2 className="font-semibold text-gray-900">Offene Nachträge ({antraege.length})</h2>
            <p className="text-xs text-gray-500 mt-0.5">Von Mitarbeitern vorgeschlagene Stempel. Genehmigen bucht den Stempel mit Nachweis im Änderungsprotokoll.</p>
          </div>
          <ul className="divide-y divide-gray-100">
            {antraege.map((a) => (
              <li key={a.id} className="p-4 flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-[220px]">
                  <div className="text-sm text-gray-900">
                    <span className="font-semibold">{a.mitarbeiterName}</span> · {a.zeitpunkt.slice(8, 10)}.{a.zeitpunkt.slice(5, 7)}.{a.zeitpunkt.slice(0, 4)} {a.zeitpunkt.slice(11)} Uhr ·{' '}
                    <span className={`text-xs px-2 py-0.5 rounded-full ${a.typ === 'KOMMEN' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                      {a.typ === 'KOMMEN' ? 'Kommen' : 'Gehen'}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 mt-0.5">„{a.begruendung}“</div>
                  <div className="text-xs text-gray-400 mt-0.5">beantragt am {a.beantragtAm.slice(8, 10)}.{a.beantragtAm.slice(5, 7)}. {a.beantragtAm.slice(11)}</div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => antragGenehmigen(a)} className="px-3 py-1.5 rounded-lg bg-green-600 text-white text-sm hover:bg-green-700">Genehmigen</button>
                  <button onClick={() => antragAblehnen(a)} className="px-3 py-1.5 rounded-lg border border-red-300 text-red-600 text-sm hover:bg-red-50">Ablehnen</button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Filter */}
      <div className="bg-white rounded-lg shadow p-4 mb-6 flex flex-wrap items-end gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Von</label>
          <DatumFeld value={von} onChange={(v) => setVon(v)} className={`p-2 border border-gray-300 rounded-lg ${von !== monatsStart() ? filterAktiv : ''}`} />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Bis</label>
          <DatumFeld value={bis} onChange={(v) => setBis(v)} className={`p-2 border border-gray-300 rounded-lg ${bis !== heute() ? filterAktiv : ''}`} />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Mitarbeiter</label>
          <select value={userId} onChange={(e) => setUserId(e.target.value === '' ? '' : Number(e.target.value))} className={`p-2 border border-gray-300 rounded-lg min-w-[200px] ${userId !== '' ? filterAktiv : ''}`}>
            <option value="">Alle</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.firstName} {u.lastName}</option>)}
          </select>
        </div>
        <button onClick={laden} className="bg-brand-600 text-white px-4 py-2 rounded-lg hover:bg-brand-700">Anzeigen</button>
        <button
          onClick={pdfExportieren}
          disabled={pdfLoading}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-brand-600 text-brand-700 hover:bg-brand-50 disabled:opacity-50"
          title={userId === '' ? 'Alle Mitarbeiter als PDF exportieren' : 'Gewählten Mitarbeiter als PDF exportieren'}
        >
          <Icon.Download className="h-4 w-4" />{pdfLoading ? 'Erzeuge PDF…' : 'Als PDF'}
        </button>
      </div>

      {loading ? (
        <LoadingScreen />
      ) : auswertung.length === 0 ? (
        <div className="bg-white rounded-lg shadow">
          <EmptyState
            icon={<Icon.Uhr className="h-6 w-6" />}
            title="Keine Daten im gewählten Zeitraum"
            action={(von !== monatsStart() || bis !== heute() || userId !== '')
              ? <FilterZuruecksetzen onClick={() => { setVon(monatsStart()); setBis(heute()); setUserId(''); }} />
              : undefined}
          />
        </div>
      ) : (
        <div className="space-y-4">
          {auswertung.map((a) => (
            <div key={a.userId} className="bg-white rounded-lg shadow overflow-clip">
              <button
                onClick={() => setExpanded(expanded === a.userId ? null : a.userId)}
                className="w-full flex flex-wrap items-center justify-between gap-3 p-4 hover:bg-gray-50 text-left"
              >
                <div className="font-semibold text-gray-900">{a.mitarbeiterName}</div>
                <div className="flex flex-wrap items-center gap-4 text-sm">
                  <span className="text-gray-500">Ist <b className="text-gray-900">{fmt(a.summeNettoMinuten)}</b></span>
                  <span className="text-gray-500">Soll <b className="text-gray-900">{fmt(a.summeSollMinuten)}</b></span>
                  <span className={`font-semibold ${saldoColor(a.saldoMinuten)}`}>{fmtSaldo(a.saldoMinuten)}</span>
                  {a.avgBeginn && <span className="text-gray-400 hidden md:inline">Ø {a.avgBeginn}–{a.avgEnde}{a.sollBeginn ? ` (Soll ${a.sollBeginn}–${a.sollEnde})` : ''}</span>}
                  <span className="text-gray-400">{expanded === a.userId ? <Icon.ChevronUp className="h-4 w-4" /> : <Icon.ChevronDown className="h-4 w-4" />}</span>
                </div>
              </button>

              {expanded === a.userId && (
                <div className={`ui-aufklappen border-t border-gray-100 ${tableScroll}`}>
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr>
                        {['Tag', 'Kommen', 'Gehen', 'Pause', 'Ist', 'Soll', 'Saldo', 'Status'].map((h) => (
                          <th key={h} className={th}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {a.tage.map((t) => (
                        <tr
                          key={t.datum}
                          onClick={() => oeffneTag({ userId: a.userId, mitarbeiterName: a.mitarbeiterName, datum: t.datum, wochentag: t.wochentag, sollBeginn: a.sollBeginn, sollEnde: a.sollEnde })}
                          title="Stempel dieses Tages korrigieren"
                          className={`cursor-pointer ${t.offen ? 'bg-amber-50 hover:bg-amber-100' : 'hover:bg-gray-50'}`}
                        >
                          <td className={`${td} whitespace-nowrap font-medium text-gray-900`}>{t.wochentag} <span className="font-normal text-gray-500">{t.datum.slice(8)}.{t.datum.slice(5, 7)}.</span></td>
                          <td className={td}>{t.kommen}</td>
                          <td className={td}>{t.gehen ?? (t.offen ? <span className="text-amber-600">offen</span> : null)}</td>
                          <td className={td}>{t.pauseMinuten > 0 ? `${t.pauseMinuten}m` : null}</td>
                          <td className={td}>{fmt(t.nettoMinuten + t.gutschriftMinuten)}</td>
                          <td className={td}>{fmt(t.sollMinuten)}</td>
                          <td className={`${td} font-medium ${saldoColor(t.saldoMinuten)}`}>{fmtSaldo(t.saldoMinuten)}</td>
                          <td className={`${td} whitespace-nowrap`}>
                            {t.feiertag && <span className="text-purple-600">{t.feiertag}</span>}
                            {t.abwesenheit && <span className="text-blue-600">{t.abwesenheit}</span>}
                            {t.bearbeitet && <span className="ml-1 inline-block align-[-2px] text-gray-500" title="durch Admin korrigiert"><Icon.Edit className="h-3.5 w-3.5" /></span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Korrektur-Modal */}
      {korrekturOffen && (
        <div className="ui-fade fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={(e) => { if (e.target === e.currentTarget) schliesseKorrektur(); }}>
          <div className="ui-pop bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="font-semibold text-gray-900">
                {korrekturTag
                  ? <>Stempel korrigieren<span className="block text-sm font-normal text-gray-500">{korrekturTag.mitarbeiterName}, {korrekturTag.wochentag} {korrekturTag.datum.slice(8)}.{korrekturTag.datum.slice(5, 7)}.{korrekturTag.datum.slice(0, 4)}</span></>
                  : `Stempel korrigieren (${von} – ${bis})`}
              </h2>
              <div className="flex gap-2">
                <button onClick={neuerEintrag} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-brand-600 text-white text-sm"><Icon.Plus className="h-4 w-4" />Stempel anlegen</button>
                <button onClick={schliesseKorrektur} className="p-1.5 text-gray-400 hover:text-gray-600" aria-label="Schließen"><Icon.Close className="h-5 w-5" /></button>
              </div>
            </div>

            {form && (
              <div className="ui-aufklappen p-4 bg-gray-50 border-b space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium mb-1">Mitarbeiter</label>
                    <select value={form.userId} disabled={!!form.id || !!korrekturTag} onChange={(e) => setForm({ ...form, userId: Number(e.target.value) })} className="w-full p-2 border rounded-lg disabled:bg-gray-100">
                      {users.map((u) => <option key={u.id} value={u.id}>{u.firstName} {u.lastName}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Typ</label>
                    <select value={form.typ} onChange={(e) => setForm({ ...form, typ: e.target.value as StempelTyp })} className="w-full p-2 border rounded-lg">
                      <option value="KOMMEN">Kommen</option>
                      <option value="GEHEN">Gehen</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Zeitpunkt</label>
                    <div className="flex gap-2">
                      <DatumFeld value={form.zeitpunkt.slice(0, 10)} onChange={(v) => setForm({ ...form, zeitpunkt: `${v}T${form.zeitpunkt.slice(11, 16)}` })} className="flex-1 p-2 border rounded-lg" aria-label="Datum" />
                      <UhrzeitFeld value={form.zeitpunkt.slice(11, 16)} onChange={(v) => setForm({ ...form, zeitpunkt: `${form.zeitpunkt.slice(0, 10)}T${v}` })} className="p-2 border rounded-lg" aria-label="Uhrzeit" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Notiz</label>
                    <input type="text" value={form.notiz} onChange={(e) => setForm({ ...form, notiz: e.target.value })} placeholder="z. B. Abmeldung vergessen" className="w-full p-2 border rounded-lg" />
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <button onClick={() => setForm(null)} className="px-3 py-1.5 rounded-lg border text-sm">Abbrechen</button>
                  <button onClick={speichern} className="px-3 py-1.5 rounded-lg bg-brand-600 text-white text-sm">Speichern</button>
                </div>
              </div>
            )}

            <div className="overflow-y-auto p-4">
              {eintraege.length === 0 ? (form ? null :
                <EmptyState title={korrekturTag ? 'Keine Stempel an diesem Tag' : 'Keine Stempel im Zeitraum'} action={<button onClick={neuerEintrag} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-brand-600 text-white text-sm"><Icon.Plus className="h-4 w-4" />Stempel anlegen</button>} />
              ) : (
                <table className="min-w-full text-sm">
                  <tbody className="divide-y divide-gray-100">
                    {eintraege.map((e) => { const glow = ankunft(`stempel-${e.id}`); return (
                      <tr key={e.id} ref={glow.ref} className={glow.className}>
                        <td className="py-2 pr-2 whitespace-nowrap font-medium text-gray-900">{korrekturTag ? `${e.zeitpunkt.slice(11)} Uhr` : e.zeitpunkt}</td>
                        {!korrekturTag && <td className="py-2 pr-2">{e.mitarbeiterName}</td>}
                        <td className="py-2 pr-2">
                          <span className={`px-2 py-0.5 rounded-full text-xs ${e.typ === 'KOMMEN' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>{e.typ === 'KOMMEN' ? 'Kommen' : 'Gehen'}</span>
                          {e.bearbeitet && <span className="ml-1 inline-block align-[-2px] text-gray-400" title="korrigiert"><Icon.Edit className="h-3.5 w-3.5" /></span>}
                        </td>
                        <td className="py-2 pr-2 text-right">
                          <button onClick={() => bearbeite(e)} className="text-brand-600 hover:underline mr-3">Bearbeiten</button>
                          <button onClick={() => loeschen(e)} className="text-red-600 hover:underline">Löschen</button>
                        </td>
                      </tr>
                    ); })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Zeiterfassung;
